import { useState, useEffect, useMemo } from 'react';
import API from '../api';

const fmt = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const fmtFecha = iso => iso ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Mismas claves que CARTERA_BUCKETS en el backend (backend/index.js).
const BUCKET_STYLE = {
  al_dia:         { color: '#059669', bg: 'rgba(5,150,105,0.14)',  border: 'rgba(5,150,105,0.35)'  },
  vencido_30:     { color: '#f59e0b', bg: 'rgba(245,158,11,0.16)', border: 'rgba(245,158,11,0.4)' },
  vencido_45:     { color: '#f97316', bg: 'rgba(249,115,22,0.16)', border: 'rgba(249,115,22,0.4)' },
  vencido_60:     { color: '#ef4444', bg: 'rgba(239,68,68,0.16)',  border: 'rgba(239,68,68,0.4)' },
  vencido_90:     { color: '#dc2626', bg: 'rgba(220,38,38,0.18)',  border: 'rgba(220,38,38,0.45)' },
  vencido_90_mas: { color: '#b91c1c', bg: 'rgba(185,28,28,0.2)',   border: 'rgba(185,28,28,0.5)'  },
};
const bucketStyle = key => BUCKET_STYLE[key] || BUCKET_STYLE.al_dia;
// Fondo fijo (no cambia con el tema) para el bloque de acento, igual al que
// usan otras tarjetas "siempre oscuras" del sistema (ej. Dashboard).
const ACCENT_BG = '#08293f';

function Badge({ bucket, label }) {
  const s = bucketStyle(bucket);
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 20,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

const fmtCompact = v => new Intl.NumberFormat('es-CO', { notation: 'compact', maximumFractionDigits: 1 }).format(v || 0);

// Columnas: cartera por antigüedad (todos los buckets, sin filtrar) — la
// distribución completa es el punto de esta gráfica.
function GraficaAntiguedad({ buckets, onSelect, filtro }) {
  const rows = (buckets || []).filter(b => b.items > 0);
  const max = rows.reduce((m, b) => Math.max(m, b.total), 1);
  if (!rows.length) return null;
  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--app-muted)', marginBottom: 14 }}>
        — Cartera por antigüedad
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 150, padding: '0 4px' }}>
        {rows.map(b => {
          const s = bucketStyle(b.key);
          const alturaPct = Math.max((b.total / max) * 100, b.total > 0 ? 4 : 0);
          const activo = filtro === b.key || (b.key === 'al_dia' && filtro === 'al_dia');
          return (
            <button
              key={b.key}
              onClick={() => onSelect(b.key)}
              title={`${b.label}: ${fmt(b.total)} (${b.items} ítem${b.items === 1 ? '' : 's'})`}
              style={{
                flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'flex-end', height: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, color: s.color, marginBottom: 4, whiteSpace: 'nowrap' }}>{fmtCompact(b.total)}</span>
              <span style={{
                width: '100%', maxWidth: 46, height: `${alturaPct}%`, minHeight: b.total > 0 ? 4 : 0,
                background: s.color, opacity: activo ? 1 : 0.75, borderRadius: '3px 3px 0 0',
                outline: activo ? `2px solid ${s.color}` : 'none', outlineOffset: 2,
                transition: 'opacity 0.15s',
              }} />
              <span style={{
                fontSize: 9, fontWeight: activo ? 800 : 600, color: activo ? 'var(--app-heading)' : 'var(--app-muted)',
                marginTop: 6, textAlign: 'center', lineHeight: 1.25, whiteSpace: 'nowrap',
              }}>{b.label.replace('Vencido ', '')}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Barras horizontales: clientes ordenados por el total ya filtrado (mismo
// criterio que la lista de abajo), asi ambas vistas siempre concuerdan.
function GraficaTopClientes({ porCliente }) {
  const rows = porCliente.slice(0, 6);
  const max = rows.reduce((m, c) => Math.max(m, c.total), 1);
  if (!rows.length) return null;
  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--app-muted)', marginBottom: 14 }}>
        — Top clientes
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map(c => {
          const s = bucketStyle(c.peorBucket);
          const anchoPct = Math.max((c.total / max) * 100, 3);
          return (
            <div key={c.cliente} title={`${c.cliente}: ${fmt(c.total)}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3, fontSize: 11 }}>
                <span style={{ color: 'var(--app-text)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.cliente}</span>
                <span style={{ color: 'var(--app-heading)', fontWeight: 700, flexShrink: 0 }}>{fmt(c.total)}</span>
              </div>
              <div style={{ height: 8, background: 'var(--app-surface-soft)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${anchoPct}%`, height: '100%', background: s.color, borderRadius: 4 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 'vencida' (por defecto, oculta lo que aun no vence) | 'todos' | una clave de bucket especifica
const FILTROS_BASE = [
  { key: 'vencida', label: 'Vencida' },
  { key: 'todos', label: 'Todo (incl. al día)' },
];

// Calificacion cualitativa del tiempo de pago (dias respecto al vencimiento).
function calificacionPago(dias) {
  if (dias <= 0) return { label: 'Puntual', color: '#059669', bg: 'rgba(5,150,105,0.14)', border: 'rgba(5,150,105,0.35)' };
  if (dias <= 15) return { label: 'Leve atraso', color: '#f59e0b', bg: 'rgba(245,158,11,0.16)', border: 'rgba(245,158,11,0.4)' };
  if (dias <= 30) return { label: 'Atraso moderado', color: '#f97316', bg: 'rgba(249,115,22,0.16)', border: 'rgba(249,115,22,0.4)' };
  return { label: 'Atraso alto', color: '#dc2626', bg: 'rgba(220,38,38,0.16)', border: 'rgba(220,38,38,0.4)' };
}

function ModalMotivo({ titulo, descripcion, onConfirm, onClose }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 16 }}>
      <div className="card" style={{ width: '100%', maxWidth: 420, margin: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--app-heading)', marginBottom: 6 }}>{titulo}</div>
        <div style={{ fontSize: 12, color: 'var(--app-muted)', marginBottom: 12, lineHeight: 1.5 }}>{descripcion}</div>
        <label style={{ display: 'grid', gap: 4, marginBottom: 14 }}>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--app-muted)' }}>Motivo (opcional)</span>
          <textarea className="input" rows={3} value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej. cliente cerró, deuda incobrable, acuerdo de condonación..." style={{ resize: 'vertical' }} />
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button
            className="btn-primary"
            style={{ background: '#dc2626', color: '#fff' }}
            disabled={enviando}
            onClick={async () => { setEnviando(true); await onConfirm(motivo); setEnviando(false); }}
          >{enviando ? 'Guardando...' : 'Castigar'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Cartera() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filtro, setFiltro] = useState('vencida');
  const [clienteAbierto, setClienteAbierto] = useState(null);
  const [modalCastigo, setModalCastigo] = useState(null); // { tipo: 'item'|'cliente', item?, cliente? }
  const [verCastigados, setVerCastigados] = useState(false);
  const [verTiempos, setVerTiempos] = useState(false);

  const cargar = () => API.get('/cartera').then(r => setData(r.data));

  useEffect(() => {
    cargar().catch(e => { setErr(e?.response?.data?.error || String(e)); setData({ total: 0, total_vencido: 0, total_al_dia: 0, total_castigado: 0, buckets: [], porCliente: [], items: [], castigados: [], tiemposPago: [] }); });
  }, []);

  async function confirmarCastigoItem(id, motivo) {
    try {
      await API.put(`/registros/${id}/castigar`, { motivo });
      setModalCastigo(null);
      await cargar();
    } catch (e) { alert(e?.response?.data?.error || 'No se pudo castigar'); }
  }

  async function confirmarCastigoCliente(cliente, motivo) {
    try {
      const r = await API.put('/cartera/cliente/castigar', { cliente, motivo });
      setModalCastigo(null);
      await cargar();
      if (r.data?.castigados) alert(`Se castigaron ${r.data.castigados} ítem(s) de ${cliente}.`);
    } catch (e) { alert(e?.response?.data?.error || 'No se pudo castigar'); }
  }

  async function descastigar(id) {
    if (!window.confirm('¿Restaurar este ítem a la cartera activa?')) return;
    try {
      await API.put(`/registros/${id}/descastigar`);
      await cargar();
    } catch (e) { alert(e?.response?.data?.error || 'No se pudo restaurar'); }
  }

  const matchFiltro = it => {
    if (filtro === 'todos') return true;
    if (filtro === 'vencida') return it.bucket !== 'al_dia';
    return it.bucket === filtro;
  };

  const { porCliente, totalFiltrado } = useMemo(() => {
    if (!data) return { porCliente: [], totalFiltrado: 0 };
    const items = (data.items || []).filter(matchFiltro);
    const map = new Map();
    items.forEach(it => {
      if (!map.has(it.cliente)) map.set(it.cliente, { cliente: it.cliente, total: 0, items: 0, peorBucket: it.bucket, peorDias: it.dias_vencido });
      const e = map.get(it.cliente);
      e.total += it.valor;
      e.items += 1;
      if (it.dias_vencido > e.peorDias) { e.peorDias = it.dias_vencido; e.peorBucket = it.bucket; }
    });
    const lista = [...map.values()].sort((a, b) => b.total - a.total);
    return { porCliente: lista, totalFiltrado: items.reduce((s, it) => s + it.valor, 0) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtro]);

  if (!data) return <div style={{ textAlign: 'center', padding: '60px 0', color: '#5a7a8a', fontSize: 13, letterSpacing: '0.06em' }}>CARGANDO...</div>;

  const itemsDeCliente = cliente => (data.items || [])
    .filter(it => it.cliente === cliente && matchFiltro(it))
    .sort((a, b) => b.dias_vencido - a.dias_vencido);

  const bucketLabel = key => data.buckets.find(b => b.key === key)?.label || key;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--app-heading)', margin: 0 }}>Cartera</h1>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--app-muted)' }}>Registros pendientes o facturados sin pagar, de cualquier año (no solo el actual). Vence al final del mes del registro. No incluye registros marcados "no facturado", igual que el Dashboard. Los registros de un mes que aún no ha llegado (plan de pagos o acuerdo a futuro) tampoco cuentan todavía.</p>
        </div>
      </div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#dc2626' }}>{err}</div>}

      {/* Totales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 16 }}>
        <div className="card" style={{ margin: 0, padding: '12px 14px', background: ACCENT_BG, border: 'none' }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(0,184,227,0.7)', marginBottom: 4 }}>Cartera total</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#00b8e3' }}>{fmt(data.total)}</div>
        </div>
        <div className="card" style={{ margin: 0, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--app-muted)', marginBottom: 4 }}>Al día (aún no vence)</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>{fmt(data.total_al_dia)}</div>
        </div>
        <div className="card" style={{ margin: 0, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--app-muted)', marginBottom: 4 }}>Vencida (por gestionar)</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>{fmt(data.total_vencido)}</div>
        </div>
      </div>

      {/* Filtro principal: vencida vs todo */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {FILTROS_BASE.map(f => {
          // "Vencida" queda seleccionada tambien cuando el usuario filtro por un bucket especifico (30/45/60/90).
          const seleccionado = f.key === 'vencida' ? filtro !== 'todos' : filtro === 'todos';
          return (
            <button key={f.key} onClick={() => setFiltro(f.key)} style={{
              fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 20, cursor: 'pointer',
              border: `1px solid ${seleccionado ? '#08293f' : 'var(--app-border)'}`,
              background: seleccionado ? ACCENT_BG : 'var(--app-surface)',
              color: seleccionado ? '#fff' : 'var(--app-text)',
            }}>{f.label}</button>
          );
        })}
      </div>

      {/* Buckets vencidos como sub-filtro */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {(data.buckets || []).filter(b => b.key !== 'al_dia' && b.items > 0).map(b => {
          const s = bucketStyle(b.key);
          const activo = filtro === b.key;
          return (
            <button key={b.key} onClick={() => setFiltro(activo ? 'vencida' : b.key)} style={{
              fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
              border: `1px solid ${activo ? s.color : s.border}`,
              background: activo ? s.color : s.bg, color: activo ? '#fff' : s.color,
            }}>{b.label} · {fmt(b.total)} ({b.items})</button>
          );
        })}
        {filtro === 'todos' && (data.buckets || []).filter(b => b.key === 'al_dia' && b.items > 0).map(b => {
          const s = bucketStyle(b.key);
          return (
            <span key={b.key} style={{
              fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 20,
              border: `1px solid ${s.border}`, background: s.bg, color: s.color,
            }}>{b.label} · {fmt(b.total)} ({b.items})</span>
          );
        })}
      </div>

      {/* Gráficas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 16 }}>
        <GraficaAntiguedad buckets={data.buckets} filtro={filtro} onSelect={key => setFiltro(f => f === key ? 'vencida' : key)} />
        <GraficaTopClientes porCliente={porCliente} />
      </div>

      {/* Por cliente */}
      <div className="card">
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--app-muted)', marginBottom: 10 }}>
          — Cartera por cliente
          {filtro === 'vencida' && ' (solo vencida)'}
          {filtro === 'todos' && ' (todo, incluye al día)'}
          {!['todos', 'vencida'].includes(filtro) && ` (filtrado: ${bucketLabel(filtro)})`}
          {' · '}{fmt(totalFiltrado)}
        </div>
        {!porCliente.length && <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--app-muted)', fontSize: 12 }}>
          {filtro === 'vencida' ? 'Sin cartera vencida. 🎉' : 'Sin cartera pendiente en este rango.'}
        </div>}
        <div style={{ display: 'grid', gap: 6 }}>
          {porCliente.map(c => {
            const abierto = clienteAbierto === c.cliente;
            const items = abierto ? itemsDeCliente(c.cliente) : [];
            return (
              <div key={c.cliente} style={{ border: '1px solid var(--app-border)', borderRadius: 4, overflow: 'hidden' }}>
                <button
                  onClick={() => setClienteAbierto(abierto ? null : c.cliente)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    padding: '10px 12px', background: 'var(--app-surface-soft)', border: 'none', cursor: 'pointer',
                    textAlign: 'left', fontFamily: 'inherit',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--app-heading)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.cliente}</span>
                    <Badge bucket={c.peorBucket} label={bucketLabel(c.peorBucket)} />
                    <span style={{ fontSize: 11, color: 'var(--app-muted)' }}>{c.items} ítem{c.items === 1 ? '' : 's'}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <b style={{ fontSize: 14, color: 'var(--app-heading)' }}>{fmt(c.total)}</b>
                    <span style={{ color: 'var(--app-muted)', fontSize: 12 }}>{abierto ? '▲' : '▼'}</span>
                  </span>
                </button>
                {abierto && (
                  <>
                    {filtro !== 'todos' && (
                      <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid var(--app-border)' }}>
                        <button
                          onClick={() => setModalCastigo({ tipo: 'cliente', cliente: c.cliente })}
                          style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 20, padding: '5px 12px', cursor: 'pointer' }}
                        >Castigar toda la cartera vencida de este cliente</button>
                      </div>
                    )}
                    <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {['Fecha', 'Servicio', 'Tipo', 'Estado', 'Vence', 'Días', 'Valor', ''].map(h => (
                            <th key={h} style={{ padding: '6px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--app-muted)', textAlign: h === 'Valor' || h === 'Días' ? 'right' : 'left', borderBottom: '1px solid var(--app-border)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(it => (
                          <tr key={it.id} style={{ borderBottom: '1px solid var(--app-border)' }}>
                            <td style={{ padding: '6px 10px', color: 'var(--app-text)' }}>{fmtFecha(it.fecha)}</td>
                            <td style={{ padding: '6px 10px', color: 'var(--app-text)' }}>{it.servicio || '—'}</td>
                            <td style={{ padding: '6px 10px', color: 'var(--app-muted)' }}>{it.tipo || '—'}</td>
                            <td style={{ padding: '6px 10px', color: 'var(--app-muted)' }}>{it.estado}</td>
                            <td style={{ padding: '6px 10px', color: 'var(--app-muted)' }}>{fmtFecha(it.fecha_vencimiento)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' }}><Badge bucket={it.bucket} label={it.dias_vencido > 0 ? `${it.dias_vencido}d` : 'Al día'} /></td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--app-heading)' }}>{fmt(it.valor)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                              <button
                                onClick={() => setModalCastigo({ tipo: 'item', item: it })}
                                title="Castigar (marcar como no cobrable)"
                                style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', background: 'none', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >Castigar</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tiempo promedio de pago */}
      {data.tiemposPago?.length > 0 && (
        <div className="card">
          <button
            onClick={() => setVerTiempos(v => !v)}
            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--app-muted)' }}>
              — Tiempo promedio de pago por cliente ({data.tiemposPago.length})
            </span>
            <span style={{ color: 'var(--app-muted)', fontSize: 12 }}>{verTiempos ? '▲' : '▼'}</span>
          </button>
          {verTiempos && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--app-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                Días entre el vencimiento (fin de mes del registro) y la fecha en que ese registro quedó marcado PAGADO. Solo clientes con 2 o más pagos históricos.
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                {data.tiemposPago.map(t => {
                  const cal = calificacionPago(t.promedio_dias);
                  return (
                    <div key={t.cliente} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 10px', border: '1px solid var(--app-border)', borderRadius: 4 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--app-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.cliente}</span>
                        <span style={{ fontSize: 10, color: 'var(--app-muted)' }}>({t.muestras} pagos)</span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 20,
                          background: cal.bg, color: cal.color, border: `1px solid ${cal.border}`,
                        }}>{cal.label}</span>
                        <b style={{ fontSize: 12, color: 'var(--app-heading)', width: 70, textAlign: 'right' }}>
                          {t.promedio_dias > 0 ? `+${t.promedio_dias}` : t.promedio_dias} día{Math.abs(t.promedio_dias) === 1 ? '' : 's'}
                        </b>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Castigos recientes */}
      {data.castigados?.length > 0 && (
        <div className="card">
          <button
            onClick={() => setVerCastigados(v => !v)}
            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--app-muted)' }}>
              — Cartera castigada ({data.castigados.length}) · {fmt(data.total_castigado)}
            </span>
            <span style={{ color: 'var(--app-muted)', fontSize: 12 }}>{verCastigados ? '▲' : '▼'}</span>
          </button>
          {verCastigados && (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Cliente', 'Servicio', 'Fecha registro', 'Castigado el', 'Motivo', 'Por', 'Valor', ''].map(h => (
                      <th key={h} style={{ padding: '6px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--app-muted)', textAlign: h === 'Valor' ? 'right' : 'left', borderBottom: '1px solid var(--app-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.castigados.map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--app-border)' }}>
                      <td style={{ padding: '6px 10px', color: 'var(--app-text)', fontWeight: 600 }}>{c.cliente}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--app-muted)' }}>{c.servicio || '—'}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--app-muted)' }}>{fmtFecha(c.fecha)}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--app-muted)' }}>{fmtFecha(c.castigado_fecha)}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--app-muted)', maxWidth: 220 }}>{c.castigado_motivo || '—'}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--app-muted)' }}>{c.castigado_usuario || '—'}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmt(c.valor)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                        <button
                          onClick={() => descastigar(c.id)}
                          title="Restaurar a la cartera activa"
                          style={{ fontSize: 10, fontWeight: 700, color: 'var(--app-muted)', background: 'none', border: '1px solid var(--app-border)', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >Restaurar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modalCastigo?.tipo === 'item' && (
        <ModalMotivo
          titulo="Castigar ítem de cartera"
          descripcion={`${modalCastigo.item.cliente} · ${modalCastigo.item.servicio || 'Sin servicio'} · ${fmt(modalCastigo.item.valor)}. Deja de contarse como cartera por cobrar; el registro y su valor histórico no se alteran.`}
          onClose={() => setModalCastigo(null)}
          onConfirm={motivo => confirmarCastigoItem(modalCastigo.item.id, motivo)}
        />
      )}
      {modalCastigo?.tipo === 'cliente' && (
        <ModalMotivo
          titulo={`Castigar toda la cartera vencida de ${modalCastigo.cliente}`}
          descripcion="Castiga de una sola vez todos los ítems ya vencidos de este cliente (no toca lo que aún esté al día). No se puede deshacer en bloque — cada ítem se puede restaurar individualmente después."
          onClose={() => setModalCastigo(null)}
          onConfirm={motivo => confirmarCastigoCliente(modalCastigo.cliente, motivo)}
        />
      )}
    </div>
  );
}
